import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageCircle, Send, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterChat = ({ newsletterContent, newsletterTitle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");

    // Add user message to chat
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/chat/discuss`,
        {
          message: userMessage,
          newsletter_context: newsletterContent
        },
        { withCredentials: true }
      );

      // Add AI response to chat
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to get response. Please try again.");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I encountered an error. Please try asking again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) {
    return (
      <Card className="shadow-lg border-2 border-purple-200">
        <CardContent className="pt-6">
          <Button
            data-testid="open-chat-btn"
            onClick={() => setIsOpen(true)}
            className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            💬 Discuss with AI
          </Button>
          <p className="text-sm text-gray-500 text-center mt-3">
            Have questions about these ideas? Chat with AI for clarification and deeper insights
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border-2 border-purple-200" data-testid="chat-container">
      <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-purple-600" />
            <CardTitle className="text-xl">Discuss with AI</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(false)}
            className="text-gray-500"
          >
            Minimize
          </Button>
        </div>
        <CardDescription>
          Ask questions about "{newsletterTitle}" or explore ideas further
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        {/* Chat Messages */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4 h-96 overflow-y-auto" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center text-gray-400 py-12">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Start a conversation about the newsletter</p>
              <p className="text-xs mt-2">Ask questions like:</p>
              <ul className="text-xs mt-2 space-y-1">
                <li>• "Can you explain the first idea in more detail?"</li>
                <li>• "What are some additional examples?"</li>
                <li>• "How can I implement this in my organization?"</li>
              </ul>
            </div>
          )}

          {messages.map((msg, index) => (
            <div
              key={index}
              className={`mb-4 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}
              data-testid={`chat-message-${index}`}
            >
              <div
                className={`inline-block max-w-[80%] p-3 rounded-lg ${
                  msg.role === 'user'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-800'
                }`}
              >
                {msg.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                    <Sparkles className="h-3 w-3" />
                    <span>AI Assistant</span>
                  </div>
                )}
                <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="text-left mb-4">
              <div className="inline-block bg-white border border-gray-200 p-3 rounded-lg">
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">AI is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="flex gap-2">
          <Input
            data-testid="chat-input"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask a question about the newsletter..."
            disabled={loading}
            className="flex-1"
          />
          <Button
            data-testid="send-message-btn"
            onClick={handleSendMessage}
            disabled={loading || !inputMessage.trim()}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default NewsletterChat;
